import React from 'react';
import { Breakpoints, TextTag } from '@volkswagen-onehub/components-core';
import {
  TextAppearance,
  TextLineHeight,
  TextSize,
  TextWeight,
} from '@vw-marketing/us-components';
import { TextBox } from '../../../../Box';
import { headingStyles } from './maps/heading';
import {
  hasStrongTag,
  hasLineBreakTag,
  processChildrenWithLineBreaks,
  is2x,
  normalizeSrc,
  isFirstChildAnIcon,
  extractIconFromWrapper,
  separateInlineAndBlock,
  hasMarkerTag,
  findFirstNonTextChild,
  removeMarkerTag,
  hasActualContent,
  isExternalUrl,
  isValidUrl,
  normalizeUrl,
} from '../helpers';
import { HeadingProps, ListProps } from '../types';
import {
  StyledIconImage,
  StyledLI,
  StyledOL,
  StyledUL,
  LIIconMarker,
  LIContent,
} from '../styles';
import { Icon } from '../../../../Icon';
import { FontWeight } from '../../../../../utils/typography';
import { CTAButton } from '../../../../Button/CTAButton';
import { onVWBasicLinkClickEvent_CTALink } from '../../../../../tagging/tagging-helpers';
import { useStore } from '../../../../../store';
import { useTrackingManager } from '../../../../../hooks/use-tracking-manager';

export const IconImage = (props: any) => {
  const [isLandscape, setIsLandscape] = React.useState<boolean | null>(null);
  const imgRef = React.useRef<HTMLImageElement>(null);
  const isRetina = is2x(props.src);

  React.useEffect(() => {
    const img = imgRef.current;
    if (img && img.complete) {
      setIsLandscape(img.naturalWidth - img.naturalHeight >= 5);
    }
  }, [props.src]);

  const handleLoad = () => {
    const img = imgRef.current;
    if (img) {
      setIsLandscape(img.naturalWidth - img.naturalHeight >= 7);
    }
  };

  return (
    <StyledIconImage
      {...props}
      ref={imgRef}
      src={normalizeSrc(props.src)}
      alt={undefined}
      $isRetina={isRetina}
      $isLandscape={isLandscape}
      onLoad={handleLoad}
    />
  );
};

export const Paragraph = (props: any) => {
  const isWrapper = props.isWrapper;
  const hasContent = hasActualContent(props.children);
  const isPreview = props.isPreview;

  return (
    <TextBox
      appearance={TextAppearance.copy200}
      tag={isWrapper || !hasContent ? TextTag.div : TextTag.p}
      style={{
        [Breakpoints.default]: {
          fontSize: TextSize.TextSize16,
          lineHeight: TextLineHeight.TextLineHeight28,
          fontWeight: TextWeight.regular,
        },
        [Breakpoints.b1920]: {
          fontSize: TextSize.TextSize18,
          lineHeight: TextLineHeight.TextLineHeight32,
        },
      }}
      customStyle={(theme) => ({
        color: theme.colors.content.tertiary,
      })}
      wrapperStyle={{
        marginTop: 0,
        marginBottom: '12px',
        ...(isWrapper ? { marginBottom: '12px' } : {}),
        ...(isPreview ? { marginBottom: 0 } : {}),
        ...(!hasContent ? { marginTop: 0, marginBottom: 0 } : {}),
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
      }}
      {...props}
    />
  );
};

export const Heading = ({
  level,
  children,
  isPreview = false,
  customStyle = {},
  ...props
}: HeadingProps) => {
  const styles = headingStyles[level];

  const hasStrong = hasStrongTag(children);
  const hasLineBreak = hasLineBreakTag(children);

  const hasContent = hasActualContent(children);

  // If no strong tag but has line breaks, process children to make last line bold
  const processedChildren =
    !hasStrong && hasLineBreak
      ? processChildrenWithLineBreaks(children)
      : children;

  const fontWeight =
    hasStrong || hasLineBreak ? FontWeight.light : FontWeight.bold;

  return (
    <TextBox
      appearance={styles.appearance}
      tag={styles.tag}
      style={{
        [Breakpoints.default]: {
          fontSize: styles.default.fontSize,
          fontWeight: TextWeight.bold,
          lineHeight: styles.default.lineHeight,
        },
        [Breakpoints.b1280]: {
          fontSize: styles.desktop.fontSize,
          fontWeight: TextWeight.bold,
          lineHeight: styles.desktop.lineHeight,
        },
      }}
      customStyle={(theme) => ({
        color: theme.colors.content.active,
        fontWeight,
      })}
      wrapperStyle={{
        margin: styles.margin,
        ...(isPreview ? { marginTop: '2px', marginBottom: '12px' } : {}),
        ...(!hasContent ? { marginTop: 0, marginBottom: 0 } : {}),
        ...customStyle,
      }}
      {...props}
    >
      {processedChildren}
    </TextBox>
  );
};

export const H1 = (props: any) => <Heading level={1} {...props} />;
export const H2 = (props: any) => <Heading level={2} {...props} />;
export const H3 = (props: any) => <Heading level={3} {...props} />;
export const H4 = (props: any) => <Heading level={4} {...props} />;
export const H5 = (props: any) => <Heading level={5} {...props} />;
export const H6 = (props: any) => <Heading level={6} {...props} />;

export const UL = ({ children, ...props }: ListProps & any) => {
  return <StyledUL {...props}>{children}</StyledUL>;
};

export const OL = ({
  start,
  children,
  ...props
}: ListProps & { start?: number } & any) => {
  const startValue = start && start > 0 ? start : undefined;

  return (
    <StyledOL {...props} start={startValue} $start={startValue}>
      {children}
    </StyledOL>
  );
};

export const LI = ({ children, ...props }: any) => {
  const childArray = React.Children.toArray(children);

  // Only check for icon marker if [@MARKER] tag is present
  if (!hasMarkerTag(children)) {
    const { inline, block } = separateInlineAndBlock(children);

    return (
      <StyledLI {...props}>
        {inline.length > 0 && <Paragraph isWrapper={true}>{inline}</Paragraph>}
        {block}
      </StyledLI>
    );
  }

  // Find the first non-text child (skip text nodes like &nbsp;)
  const { index: firstChildIndex, child: firstChild } =
    findFirstNonTextChild(childArray);

  const isFirstChildIcon = firstChild
    ? isFirstChildAnIcon(firstChild, true)
    : false;

  if (isFirstChildIcon && firstChild) {
    // Extract the icon from wrapper if needed (e.g., when inside strong tag)
    // Skip nested lists when extracting
    const { icon, remainingWrapperChildren } = extractIconFromWrapper(
      firstChild,
      true
    );
    const iconElement = icon || firstChild;

    // Preserve text nodes before first child, then remaining from wrapper, then siblings after
    const textBefore = childArray.slice(0, firstChildIndex);
    const siblingsAfter = childArray.slice(firstChildIndex + 1);
    const remainingChildren = [
      ...textBefore,
      ...remainingWrapperChildren,
      ...siblingsAfter,
    ];

    // Remove [@MARKER] tag from remaining children
    const cleanedChildren = React.Children.map(
      remainingChildren,
      removeMarkerTag
    );

    const { inline, block } = separateInlineAndBlock(cleanedChildren);

    return (
      <StyledLI $hasIconMarker={true} {...props} data-icon-marker='true'>
        <LIIconMarker>{iconElement}</LIIconMarker>
        <LIContent>
          {inline.length > 0 && (
            <Paragraph isWrapper={true}>{inline}</Paragraph>
          )}
          {block}
        </LIContent>
      </StyledLI>
    );
  }

  const { inline, block } = separateInlineAndBlock(children);

  return (
    <StyledLI {...props}>
      {inline.length > 0 && <Paragraph isWrapper={true}>{inline}</Paragraph>}
      {block}
    </StyledLI>
  );
};

export const Strong = ({ children, ...props }: any) => {
  const hasLineBreak = hasLineBreakTag(children);

  return (
    <TextBox
      appearance={TextAppearance.copy200}
      tag={TextTag.strong}
      style={{
        [Breakpoints.default]: {
          fontSize: hasLineBreak ? TextSize.TextSize18 : TextSize.TextSize16,
          fontWeight: TextWeight.bold,
          lineHeight: TextLineHeight.TextLineHeight28,
        },
        [Breakpoints.b1920]: {
          fontSize: hasLineBreak ? TextSize.TextSize20 : TextSize.TextSize18,
          fontWeight: TextWeight.bold,
          lineHeight: TextLineHeight.TextLineHeight32,
        },
      }}
      {...props}
    >
      {children}
    </TextBox>
  );
};

export const CTALink = (props: any) => {
  const [state] = useStore();
  const { qsgNavigation } = state.qsg;
  const trackingManager = useTrackingManager();

  const model = qsgNavigation
    ? {
        modelId: qsgNavigation.modelId,
        modelName: qsgNavigation.modelName,
        modelYear: qsgNavigation.modelYear,
      }
    : undefined;

  const originalHref = props.href || '#';
  const isExternal = isExternalUrl(originalHref);
  const isValid = isValidUrl(originalHref);

  // If URL is invalid, use a safe href that won't navigate
  const href = isValid ? normalizeUrl(originalHref) : '#';

  // Prevent navigation for invalid URLs
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!isValid) {
      e.preventDefault();
      e.stopPropagation();
    } else {
      onVWBasicLinkClickEvent_CTALink(trackingManager, href, isExternal, model);
    }
  };

  return (
    <CTAButton
      tag='a'
      emphasis='tertiary'
      href={href}
      target={isExternal && isValid ? '_blank' : undefined}
      rel={isExternal && isValid ? 'noopener nofollow' : undefined}
      onClick={handleClick}
    >
      {props.children}
      {isExternal && isValid && (
        <>
          {' '}
          <Icon
            name='ExternalLink'
            style={{ width: '13.6px', height: '11.2px' }}
            ignoreDefaultColor={true}
          />
        </>
      )}
    </CTAButton>
  );
};

export const Superscript = ({ children, ...props }: any) => {
  return (
    <TextBox
      appearance={TextAppearance.copy200}
      tag={TextTag.sup}
      style={{
        [Breakpoints.default]: {
          fontSize: TextSize.TextSize14,
          lineHeight: TextLineHeight.TextLineHeight22,
          fontWeight: TextWeight.regular,
        },
        [Breakpoints.b1920]: {
          fontSize: TextSize.TextSize16,
          lineHeight: TextLineHeight.TextLineHeight24,
        },
      }}
      {...props}
    >
      {children}
    </TextBox>
  );
};

export const Subscript = ({ children, ...props }: any) => {
  return (
    <TextBox
      appearance={TextAppearance.copy200}
      tag={TextTag.sub}
      style={{
        [Breakpoints.default]: {
          fontSize: TextSize.TextSize14,
          lineHeight: TextLineHeight.TextLineHeight22,
          fontWeight: TextWeight.regular,
        },
        [Breakpoints.b1920]: {
          fontSize: TextSize.TextSize16,
          lineHeight: TextLineHeight.TextLineHeight24,
        },
      }}
      customStyle={
        {
          //verticalAlign: 'super',
        }
      }
      {...props}
    >
      {children}
    </TextBox>
  );
};
